/**
 * ExperimentalOnboardingModal - Experimental Application Onboarding Wizard
 *
 * Uses REST API with Backstage Signals for real-time provisioning updates:
 * - POST /api/provisioning/experimental-apps - Create application
 *
 * Refactored version using modular hooks and step components for better maintainability.
 */
import { useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import {
  DevHubBox,
  DevHubGrid,
  DevHubTypography,
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubIconButton,
  DevHubButton,
} from '@devxdiscover/devhub-ui';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { OnboardingModalProps } from './onboarding-modal/types';
import { STEPS } from './onboarding-modal/constants';
import {
  useOnboardingForm,
  useProvisioningState,
  useSignalHandling,
  useProvisioningAPI,
} from './onboarding-modal/hooks';
import {
  WelcomeStep,
  ReviewStep,
  ProvisioningStep,
} from './onboarding-modal/steps';

/**
 * ExperimentalOnboardingModal component that displays a stepper-based onboarding experience
 * @param props - The component props
 */
export const ExperimentalOnboardingModal = ({
  open,
  onClose,
  onSuccess,
  onViewIdeas,
}: OnboardingModalProps) => {
  // Step navigation state
  const [activeStep, setActiveStep] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);

  // Custom hooks for form, provisioning, and API
  const formHook = useOnboardingForm();
  const provisioningHook = useProvisioningState();
  const identityApi = useApi(identityApiRef);

  const { setCurrentTaskId } = useSignalHandling(
    provisioningHook.updateProvisioningComponent,
  );

  const { createApplicationAndProvision, retryProvisioning } =
    useProvisioningAPI(
      provisioningHook.updateProvisioningComponent,
      provisioningHook.setMutationError,
      setCurrentTaskId,
      provisioningHook.provisioningComponents,
    );

  /**
   * Handles closing the modal and resetting all state
   */
  const handleClose = () => {
    setIsClosing(true);
    setActiveStep(0);
    setHasRetried(false);
    formHook.resetForm();
    provisioningHook.resetProvisioning();
    onClose();
    setTimeout(() => setIsClosing(false), 100);
  };

  /**
   * Handles the "Done" action after successful provisioning
   */
  const handleDone = () => {
    handleClose();
    if (onSuccess && provisioningHook.createdAppData) {
      onSuccess(provisioningHook.createdAppData);
    }
  };

  /**
   * Handles the "View My Ideas" action
   * Stores created app data in sessionStorage and navigates to Action Center
   */
  const handleViewIdeas = () => {
    if (provisioningHook.createdAppData) {
      sessionStorage.setItem(
        'devhub_newly_created_app',
        JSON.stringify(provisioningHook.createdAppData),
      );
    }

    if (onViewIdeas) {
      onViewIdeas();
    }

    if (onSuccess && provisioningHook.createdAppData) {
      onSuccess(provisioningHook.createdAppData);
    }

    handleClose();
  };

  /**
   * Handles the "Onboard Another" action
   * Resets the wizard to step 1 without closing
   */
  const handleOnboardAnother = () => {
    setActiveStep(0);
    formHook.resetForm();
    provisioningHook.resetProvisioning();
  };

  /**
   * Handles the "Continue" action from step 1
   * Validates form and proceeds to review step
   */
  const handleContinue = () => {
    if (formHook.isFormValid()) {
      setActiveStep(1);
    } else {
      formHook.touchAllFields();
    }
  };

  /**
   * Handles the "Back" action
   */
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  /**
   * Handles form submission
   * Creates application and starts provisioning
   */
  const handleSubmit = async () => {
    setActiveStep(2);
    provisioningHook.resetProvisioning();
    setIsSubmitting(true);

    const trimmedData = formHook.getTrimmedFormData();

    try {
      const identity = await identityApi.getBackstageIdentity();
      const userLogin = identity.userEntityRef.split('/')[1];

      await createApplicationAndProvision(
        trimmedData.name,
        trimmedData.description,
        userLogin,
      );
    } catch (error) {
      // Error handling is done in useProvisioningAPI hook
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles retry action for failed provisioning
   * Re-triggers the provisioning process with the same form data
   * Uses smart reset to preserve completed resources and their data
   */
  const handleRetry = async () => {
    setHasRetried(true);
    provisioningHook.setIsRetrying(true);
    provisioningHook.resetProvisioningForRetry();
    setIsSubmitting(true);

    const trimmedData = formHook.getTrimmedFormData();

    try {
      const identity = await identityApi.getBackstageIdentity();
      const userLogin = identity.userEntityRef.split('/')[1];

      await retryProvisioning(
        trimmedData.name,
        trimmedData.description,
        userLogin,
      );
    } catch (error) {
      // Error handling is done in useProvisioningAPI hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DevHubDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        style: {
          width: '600px',
          maxWidth: '600px',
        },
      }}
    >
      <DevHubDialogTitle>
        <DevHubGrid
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <DevHubTypography variant="h5" weight="light" fontColor="purpleBlue">
            {STEPS[activeStep]}
          </DevHubTypography>
          <DevHubIconButton onClick={handleClose}>
            <CloseIcon />
          </DevHubIconButton>
        </DevHubGrid>
      </DevHubDialogTitle>

      <DevHubDialogContent style={{ minHeight: '220px' }}>
        <DevHubBox padding="medium">
          {activeStep === 0 && (
            <WelcomeStep
              formData={formHook.formData}
              touchedFields={formHook.touchedFields}
              onFormChange={formHook.onFormChange}
              handleFieldBlur={formHook.handleFieldBlur}
              nameUniquenessError={formHook.nameUniquenessError}
              setNameUniquenessError={formHook.setNameUniquenessError}
              isCheckingName={formHook.isCheckingName}
              setIsCheckingName={formHook.setIsCheckingName}
            />
          )}
          {activeStep === 1 && <ReviewStep formData={formHook.formData} />}
          {activeStep === 2 && (
            <ProvisioningStep
              formData={formHook.formData}
              mutationError={provisioningHook.mutationError}
              overallProgress={provisioningHook.overallProgress}
              provisioningComponents={provisioningHook.provisioningComponents}
              createdAppData={provisioningHook.createdAppData}
              adGroups={provisioningHook.adGroups}
              githubRepos={provisioningHook.githubRepos}
              isRetrying={provisioningHook.isRetrying}
              isFailed={provisioningHook.isFailed}
              hasRetried={hasRetried}
              onRetry={handleRetry}
            />
          )}
        </DevHubBox>
      </DevHubDialogContent>

      <DevHubDialogActions
        style={{ padding: '16px 24px', justifyContent: 'space-between' }}
      >
        {/* Error state buttons */}
        {activeStep === 2 && provisioningHook.mutationError && (
          <DevHubGrid container justifyContent="flex-end" spacing={2}>
            <DevHubGrid item>
              <DevHubButton
                variant="outlined"
                size="medium"
                onClick={handleClose}
              >
                Cancel
              </DevHubButton>
            </DevHubGrid>
            <DevHubGrid item>
              <DevHubButton
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleBack}
              >
                Try Again
              </DevHubButton>
            </DevHubGrid>
          </DevHubGrid>
        )}

        {/* Normal flow buttons */}
        {!isClosing && activeStep !== 2 && (
          <>
            <DevHubBox>
              {activeStep === 1 && (
                <DevHubBox style={{ display: 'flex', gap: '8px' }}>
                  <DevHubButton variant="outlined" onClick={handleClose}>
                    Cancel
                  </DevHubButton>
                  <DevHubButton variant="outlined" onClick={handleBack}>
                    Back
                  </DevHubButton>
                </DevHubBox>
              )}
            </DevHubBox>
            <DevHubBox>
              {activeStep === 0 && (
                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!formHook.isFormValid()}
                  onClick={handleContinue}
                >
                  Continue
                </DevHubButton>
              )}
              {activeStep === 1 && (
                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  data-gtm-event="experimental-onboarding-submit"
                >
                  {isSubmitting ? 'Creating...' : 'Go'}
                </DevHubButton>
              )}
            </DevHubBox>
          </>
        )}

        {/* Success state buttons - enable when provisioning completes OR fails */}
        {activeStep === 2 && !provisioningHook.mutationError && (
          <DevHubGrid container justifyContent="flex-end" spacing={2}>
            <DevHubGrid item>
              <DevHubButton
                variant="outlined"
                size="medium"
                onClick={handleDone}
                disabled={
                  !provisioningHook.provisioningComplete &&
                  !provisioningHook.isFailed
                }
              >
                Done
              </DevHubButton>
            </DevHubGrid>
            <DevHubGrid item>
              <DevHubButton
                variant="outlined"
                size="medium"
                onClick={handleOnboardAnother}
                disabled={
                  !provisioningHook.provisioningComplete &&
                  !provisioningHook.isFailed
                }
              >
                Onboard Another
              </DevHubButton>
            </DevHubGrid>
            <DevHubGrid item>
              <DevHubButton
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleViewIdeas}
                disabled={
                  !provisioningHook.provisioningComplete &&
                  !provisioningHook.isFailed
                }
              >
                View My Ideas
              </DevHubButton>
            </DevHubGrid>
          </DevHubGrid>
        )}
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
